import React from "react";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  CreditCard,
  Key,
  Monitor,
  Receipt,
  FileText,
  Settings,
  LogOut,
  ShieldAlert,
  Headphones,
  ExternalLink,
  ChevronRight,
} from "lucide-react";
import { useMasterAdmin } from "../../context/MasterAdminAuthContext";

export default function MasterAdminLayout() {
  const { masterAdmin, logout } = useMasterAdmin();
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { label: "Dashboard", path: "/master-admin", icon: LayoutDashboard, exact: true },
    { label: "User Management", path: "/master-admin/users", icon: Users },
    { label: "Memberships (₹99)", path: "/master-admin/memberships", icon: CreditCard },
    { label: "Licenses", path: "/master-admin/licenses", icon: Key },
    { label: "Device Binding", path: "/master-admin/devices", icon: Monitor },
    { label: "Payments", path: "/master-admin/payments", icon: Receipt },
    { label: "Audit Logs", path: "/master-admin/audit-logs", icon: FileText },
    { label: "System Settings", path: "/master-admin/settings", icon: Settings },
  ];

  const handleLogout = () => {
    logout();
    navigate("/master-admin/login");
  };

  return (
    <div className="flex h-screen bg-zinc-950 text-zinc-100 overflow-hidden font-sans selection:bg-amber-500/30">
      {/* Sidebar */}
      <aside className="w-64 bg-zinc-900/95 border-r border-zinc-800/80 flex flex-col justify-between shrink-0 z-20">
        {/* Brand */}
        <div>
          <div className="p-5 border-b border-zinc-800/80 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-amber-400 shadow-md">
                <ShieldAlert className="w-5 h-5" />
              </div>
              <div>
                <div className="text-sm font-extrabold tracking-tight text-white flex items-center gap-1">
                  <span>Print</span>
                  <span className="text-amber-400">Q</span>
                  <span className="text-[10px] px-1.5 py-0.2 rounded bg-amber-500/15 text-amber-400 font-bold border border-amber-500/30 ml-1">
                    MASTER
                  </span>
                </div>
                <div className="text-[10px] text-zinc-400 font-medium">
                  JSM DIGITAL SOLUTIONS
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Links */}
          <nav className="p-3 flex flex-col gap-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = item.exact
                ? location.pathname === item.path
                : location.pathname.startsWith(item.path);

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs font-semibold transition-all ${
                    isActive
                      ? "bg-amber-500/15 text-amber-300 border border-amber-500/30 shadow-sm shadow-amber-500/10 font-bold"
                      : "text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/60 border border-transparent"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Icon className={`w-4 h-4 ${isActive ? "text-amber-400" : "text-zinc-400"}`} />
                    <span>{item.label}</span>
                  </div>
                  {isActive && <ChevronRight className="w-3.5 h-3.5 text-amber-400/80" />}
                </Link>
              );
            })}
          </nav>
        </div>

        {/* Bottom Profile & Actions */}
        <div className="p-3 border-t border-zinc-800/80 flex flex-col gap-2">
          {/* Admin Info */}
          <div className="bg-zinc-950/80 border border-zinc-800/80 rounded-xl p-2.5 flex items-center justify-between">
            <div className="flex items-center gap-2 overflow-hidden">
              <div className="w-7 h-7 rounded-lg bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400 text-xs font-bold shrink-0">
                {masterAdmin?.name ? masterAdmin.name[0] : "M"}
              </div>
              <div className="truncate">
                <div className="text-xs font-bold text-white truncate">{masterAdmin?.name || "Master Admin"}</div>
                <div className="text-[10px] text-amber-400/90 truncate font-mono">@{masterAdmin?.username || "admin"}</div>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="p-1.5 rounded-lg text-zinc-400 hover:text-rose-400 hover:bg-rose-500/10 transition-colors"
              title="Logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>

          {/* Quick link to standard shop app */}
          <Link
            to="/login"
            className="flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl bg-zinc-950 hover:bg-zinc-800 border border-zinc-800 text-[11px] text-zinc-400 hover:text-white transition-colors"
          >
            <span>Open Shop Login</span>
            <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Header */}
        <header className="h-14 bg-zinc-900/60 border-b border-zinc-800/80 px-6 flex items-center justify-between shrink-0 backdrop-blur-md">
          <div className="flex items-center gap-2 text-xs text-zinc-400">
            <span className="text-zinc-500">Master Control</span>
            <span>/</span>
            <span className="text-white font-medium capitalize">
              {location.pathname === "/master-admin"
                ? "Dashboard Overview"
                : location.pathname.split("/").pop().replace("-", " ")}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[11px] font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span>MongoDB Connected</span>
            </div>

            <div className="flex items-center gap-1.5 text-xs text-zinc-400 font-medium px-2.5 py-1 rounded-lg bg-zinc-900 border border-zinc-800">
              <Headphones className="w-3.5 h-3.5 text-amber-400" />
              <span>Support Lead: Kunal Warkante (9545262332)</span>
            </div>
          </div>
        </header>

        {/* Viewport for sub-pages */}
        <main className="flex-1 overflow-y-auto p-6 bg-zinc-950">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
